import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import { ColumnMap } from 'src/app/models/Product/Import/ColumnMap.model';
import { AlertMessageService } from '../../alert-message/alert-message.service';
import * as XLSX from 'xlsx';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Observable, Subscription, defer, filter, finalize, from, fromEvent, repeat, startWith, switchMap, take, takeUntil, takeWhile, timer } from 'rxjs';
import { ProductImportation } from 'src/app/models/Product/Import/ProductImportation.model';
import { ImportedProductStatusEnum } from 'src/app/Enums/ImportedProductStatusEnum';
import { ImportedProduct } from 'src/app/models/Product/Import/ImportedProduct.model';
import { ProductImportationStatusEnum } from 'src/app/Enums/ProductImportationStatusEnum';
import { MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS_FACTORY } from '@angular/material/progress-spinner';
import { Product } from 'src/app/models/Product/ProductRegister.model';

@Injectable({
  providedIn: 'root'
})
export class ProductImportationService {

  // #region Variables Declarations

  private _file: File | null = null;
  public getFile(): File | null { return this._file; }

  private _importation: ProductImportation | null = null;
  private _mapColumnsSubscription: Subscription | null = null;
  public cancelOnDestroy: boolean = false;

  private _fields: BehaviorSubject<{value: string, text: string, disabled: boolean}[]> = new BehaviorSubject<{value: string, text: string, disabled: boolean}[]>(Object.assign([]));
  public getFields(): Observable<{value: string, text: string, disabled: boolean}[]> { return this._fields.asObservable(); }
  public setFields(fields: {value: string, text: string, disabled: boolean}[]) {
    fields = [...[{value: '', text: "Selecione", disabled: false}], ...fields];
    this._fields.next(fields);
  }

  private _columnMap: BehaviorSubject<ColumnMap> = new BehaviorSubject<ColumnMap>(Object.assign({}));
  public getColumnMap(): Observable<ColumnMap> { return this._columnMap.asObservable(); }
  public setColumnMap(map: ColumnMap) {
    this._columnMap.next(map);
  }

  private _fileData: string[] | null = null;

  private _firstTenRows: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(Object.assign([]));
  public getFirstTenRows(): Observable<string[]> { return this._firstTenRows.asObservable(); }
  public setFirstTenRows(rows: string[]) { 
    this._firstTenRows.next(rows);
  }

  private _totalItemsToShow: BehaviorSubject<number|null> = new BehaviorSubject<number|null>(Object.assign({}));
  public getTotalItemsToShow(): Observable<number|null> {return this._totalItemsToShow.asObservable();};
  public setTotalItemsToShow(value: number){this._totalItemsToShow.next(value);}

  private _totalNew: BehaviorSubject<number|null> = new BehaviorSubject<number|null>(Object.assign({}));
  public getTotalNew(): Observable<number|null> {return this._totalNew.asObservable();};
  public setTotalNew(value: number){this._totalNew.next(value);}

  private _totalExisting: BehaviorSubject<number|null> = new BehaviorSubject<number|null>(Object.assign({}));
  public getTotalExisting(): Observable<number|null> {return this._totalExisting.asObservable();};
  public setTotalExisting(value: number){this._totalExisting.next(value);}

  private _totalError: BehaviorSubject<number|null> = new BehaviorSubject<number|null>(Object.assign({}));
  public getTotalError(): Observable<number|null> {return this._totalError.asObservable();};
  public setTotalError(value: number){this._totalError.next(value);}

  private _validatedProducts: BehaviorSubject<ImportedProduct[] | null> = new BehaviorSubject<ImportedProduct[] | null>(Object.assign([]));
  public setProducts(value: ImportedProduct[] | null) { this._validatedProducts.next(value); }
  public getProducts(): Observable<ImportedProduct[] | null> { return this._validatedProducts.asObservable(); }

  private _importationHistory: BehaviorSubject<ProductImportation[]> = new BehaviorSubject<ProductImportation[]>(Object.assign([]));
  private setImportationHistory(value: ProductImportation[]) { this._importationHistory.next(value); }
  public getImportationHistory(): Observable<ProductImportation[]> { return this._importationHistory.asObservable(); }

  // #endregion Attributes Declarations

  constructor(
    private api: ApiService,
    private alert: AlertMessageService,
    private router: Router,
    private translate: TranslateService
  ) { }

  public async readFile(file: File): Promise<string[] | null> {
    let data: string[] | Error = [];
    if (file.size > 30000000) {
      this.alert.alertErrorMessage(this.translate.instant("product.import.errors.file-size", { filename: file.name, filesize: Math.trunc(file.size / 1000000)}));
      return null;
    }

    let fileExtension = file.name.split(".").pop();
    switch (fileExtension) {
      case "xls":
      case "xlsx": {
        data = await this.readExcelFile(file);
        break;
      }
      case "csv": {
        data = await this.readTextFile(file);
        break;
      }
      default: {
        this.alert.alertErrorMessage(this.translate.instant("product.import.errors.file-extension"));
        return null;
      }
    }

    if (data instanceof Error) {
       if (data.message.includes("password") || data.message.includes("encrypted") || data.message=="File is password-protected") 
           this.alert.alertErrorMessage(this.translate.instant("product.import.errors.password-protected"));
        else
          this.alert.alertErrorMessage(this.translate.instant("product.import.errors.unable-read-file"));
      return null;
    }

    const filledRows = data.filter((row) => row.replace(/;/g, "").trim() !== "").length;

    if (filledRows < 2) {
      this.alert.alertErrorMessage(this.translate.instant("product.import.errors.minimum-rows"));
      return null;
    }

    if (filledRows > 100001) {
      this.alert.alertErrorMessage(this.translate.instant("product.import.errors.maximum-products"));
      return null;
    }

    this.setFirstTenRows([]); // limpa a grade da tela de mapeamento
    this._file = file;
    this._fileData = data;

    return data;
  }

  public async loadOriginalFileData(importationId: number) {
    this.apiGetProductImportationOriginalFileData(importationId).then(data => {
      this._fileData = data;
      if (this._fileData != null)
        this.mapColumns(this._fileData);
    });
  }

  public async mapColumns(data: string[]) {
    let headerRowColumns: string[] = this.removeHeaderEmptyColumnsAtEnd(data[0] ?? "").split(";");
    if (headerRowColumns.length < 3) {
      this.alert.alertErrorMessage(this.translate.instant("product.import.errors.minimum-columns"));
      setTimeout(()=>{this.router.navigate(['/product'],{skipLocationChange:false})}, 100);
      return;
    }

    this._mapColumnsSubscription?.unsubscribe();
    this._mapColumnsSubscription = this.getColumnMap().subscribe((map)=>{
      this.convertRawDataToFirstTen(map, data);
    });

    this.apiGetColumnMap(headerRowColumns.join(";")).then((map)=>{
      if (map == null) {
        return;
      }
      this.setColumnMap(map);
      this.convertRawDataToFirstTen(map, data);
    });
  }

  public async saveColumnMap(map: ColumnMap) {
    await this.apiPostColumnMap(map);
  }

  public async loadColumnMap(headRowColumns: string) {
    this.apiGetColumnMap(headRowColumns).then((map) => {
      if (map != null)
      this.setColumnMap(map);
    });
  }

  public async sendFile() {
    const file = this._file;
    this.setProducts(null);

    if (!file) {
      this.alert.alertErrorMessage("Erro ao enviar arquivo");
      this.router.navigate(['/product'],{skipLocationChange:false});
      return;
    }

    let importation: ProductImportation | null = await this.apiPostImportFromFile(file, this._importation?.id);
    if (importation == null || importation.id == null){
      this.alert.alertErrorMessage("Erro ao validar produtos");
      this.router.navigate(['/product'],{skipLocationChange:false});
      return;
    }

    this._importation = importation;
  }

  public async reviewValidatedProducts(importationId: number) {
    if (!(importationId > 0)) {
      return;
    }

    const needsFileData = !this._fileData || this._fileData.length == 0;
    const [importation, data] = await Promise.all([
      this.apiGetProductImportation(importationId),
      needsFileData ? this.apiGetProductImportationOriginalFileData(importationId) : Promise.resolve(null)
    ]);
    if (importation == null) {
      // #TODO Exception
      return;
    }
    this._importation = importation;

    if (needsFileData) {
      if (!data) {
        // #TODO Exception
        return;
      }
      this._fileData = data;
    }
  }

  public async loadValidatedProducts(importationId: number, pageSize?: number | null, page?: number | null, filter?: ImportedProductStatusEnum | null) {   
    let ret = await this.apiGetValidatedProducts(importationId, pageSize, page, filter);

    this.setProducts(ret['data']);
    this.setTotalItemsToShow(ret['totalItemsToShow']);
    this.setTotalNew(ret['totalNew']);
    this.setTotalExisting(ret['totalExisting']);
    this.setTotalError(ret['totalError']);
  }

  public async cancelImportation() {
    if (this._importation != null) {
      this.apiPostCancelImportation(this._importation.id);
    }
  }
  
  public clear() {
    this._importation = null;
    this.setFields([]);
    this.setProducts([]);
    this.setFirstTenRows([]);
  }

  public async confirmImportation(): Promise<boolean> {
    this.setProducts([]);

    if (this._importation == null) {
      this.alert.alertErrorMessage("Erro ao confirmar importação");
      return false;
    }

    let ret: ProductImportation | null = null;
    ret = await this.apiPostConfirmImportation(this._importation.id);

    if (ret != null) {
      this._importation = ret;
      return true;
    }

    return false;
  }

  public async loadProductImportation(importationId: number): Promise<void> {
    if (importationId <= 0) return;
    this._importation = await this.apiGetProductImportation(importationId);
  }

  private static readonly pollDelaysMs = [1000, 2000, 4000, 8000, 10000];

  public pollWhileVisible<T>(fetch: () => Promise<T>): Observable<T> {
    const visible$ = fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      filter(() => document.visibilityState === 'visible'),
      take(1)
    );
    const delays = ProductImportationService.pollDelaysMs;

    return defer(() => visible$.pipe(switchMap(() => from(fetch())))).pipe(
      repeat({ delay: (count) => timer(delays[Math.min(count - 1, delays.length - 1)]) })
    );
  }

  public watchImportation(importationId: number, finalStatuses: ProductImportationStatusEnum[]): Observable<ProductImportation> {
    if (importationId <= 0) return EMPTY;

    return this.pollWhileVisible(async () => {
        try {
          await this.loadProductImportation(importationId);
          return await this.getProductImportation();
        } catch {
          return null;
        }
      }).pipe(
      filter((importation): importation is ProductImportation => importation != null),
      takeWhile((importation) => !finalStatuses.includes(importation.status), true)
    );
  }

  public async getProductImportation(): Promise<ProductImportation | null> {
    return this._importation;
  }

  private readTextFile(file: File): Promise<string[] | Error> {
    let fileReader = new FileReader();

    return new Promise((resolve, reject) => {
      
      fileReader.onerror = (e) => {
        this.alert.alertErrorMessage("Erro ao ler arquivo.");
        this.router.navigate(['/product'],{skipLocationChange:false});
        reject();
      }

      fileReader.onload = (e) => {
        try {
          let workbook = XLSX.read(fileReader.result, {type: 'string', codepage: 65001});
          let sheet = workbook.Sheets[workbook.SheetNames[0]];
          let result = XLSX.utils.sheet_to_csv(sheet,
            { FS: ";", RS: "\r\n", strip: true, rawNumbers: true }
          );
          resolve(result?.split("\r\n"));
        } catch (err: any) {
          resolve((<Error>err));
        }
      }

      fileReader.readAsText(file);
    });
  }

  private readExcelFile(file: File): Promise<string[] | Error>{
    let buffer: Buffer;
    let fileReader = new FileReader();

    return new Promise((resolve, reject) => {
      fileReader.onerror = (e) => {
        this.alert.alertErrorMessage("Erro ao ler arquivo.");
        reject();
      }

      fileReader.onload = (e) => {
        try {
          buffer = fileReader.result as unknown as Buffer;
          let workbook = XLSX.read(buffer, {});
          let sheet = workbook.Sheets[workbook.SheetNames[0]];
          let result = XLSX.utils.sheet_to_csv(sheet,
            { FS: ";", RS: "\r\n", strip: true, rawNumbers: true }
          );
          resolve(result?.split("\r\n"));
        } catch (err: any) {
          resolve((<Error>err));
        }
      }

      fileReader.readAsArrayBuffer(file);
    });
  }

  private async convertRawDataToFirstTen(map: ColumnMap, data: string[]): Promise<void> {
    const headerRowColumns = map.headerRow?.split(";") ?? [];

    const firstTenRows = data.slice(1,11);
    var mappedColumns: string[] = []; // nome do sistema das colunas ordenadas como estão na planilha;
    var fieldsOptions: {value: string, text: string, disabled: boolean}[] = []; // opcoes dos mat-selects
    
    headerRowColumns.forEach((headerRowColumn, idx)=>{
      headerRowColumn = headerRowColumn.trim();
      const selectedMapField = Object.entries(map).find(([key, val])=>{return val == headerRowColumn});

      if (selectedMapField == undefined) {
        if (headerRowColumn != "") {
          fieldsOptions.push({value: headerRowColumn, text: headerRowColumn, disabled: false});
        }
        mappedColumns.push("");
      } else {
        if (headerRowColumn != ""){
          fieldsOptions.push({value: headerRowColumn, text: headerRowColumn, disabled: true});
        }
        let fieldName = selectedMapField[0].replace("field", ""); // fieldBarCode -> BarCode
        const mappedCol = fieldName.charAt(0).toLowerCase().concat(fieldName.substring(1)) ?? headerRowColumn;
        mappedColumns.push(mappedCol); // BarCode -> barCode
      }
    });

    this.setFields(fieldsOptions);

    let mappedData = <any>[]; // Dados a serem exibidos na tela de mapear colunas
    for (let rowIdx = 0; rowIdx < firstTenRows.length; rowIdx++) {
      let row = firstTenRows[rowIdx];
      let cells = row.split(";");
      let obj = <any>{};
      for (let i = 0; i < cells.length; i++) {
        obj[mappedColumns[i]] = cells[i]; // ex: obj["barCode"] = cells[0]
      }
      mappedData.push(obj);
    }

    this.setFirstTenRows(mappedData);
  }

  // #region Api Calls Methods

  private async apiGetColumnMap(headRow: string): Promise<ColumnMap | null> {
    let r = await this.api.get<ColumnMap>("/v1/product/importation/column-map", headRow == "" ? null : {headerColumns: headRow});
    if (!r.success ) {
      this.alert.alertErrorMessage(r.message ?? this.translate.instant("api.notFound"));
      return null;
    }
    return r.data;
  }

  private async apiPostColumnMap(map: ColumnMap): Promise<boolean> {
    let r = await this.api.post<any>("/v1/product/importation/column-map", map);
    if (!r.success) {
      this.alert.alertErrorMessage(r.message ?? this.translate.instant("api.internalServerError"));
      return false;
    }
    return true;
  }

  private async apiPostImportFromFile(file: File, importationId?: number): Promise<ProductImportation | null> {
    let data = new FormData();
    data.append("file", file);
    let route = importationId != undefined && importationId > 0 ? "/v1/product/importation/"+importationId+"/import-from-file" : "/v1/product/importation/import-from-file";
    let r = await this.api.post<any>(route, data);
    if (!r.success) {
      this.alert.alertErrorMessage(r.message ?? this.translate.instant("api.internalServerError"));
      return null;
    }
    return r.data;
  }

  public async apiGetProductImportation(id: number): Promise<ProductImportation | null> {
    let r = await this.api.get<any>("/v1/product/importation/"+id.toString());
    if (r != null && r.success){
      this._importation = r.data;
      return r.data;
    }
    return null;
  }

  public async apiGetProductImportationPaginated(page: number = 0, pageSize: number = 5, importationStatus: ProductImportationStatusEnum | null = null): Promise<void> {
    let params: any = { page: page, pageSize: pageSize };
    if (importationStatus != null)
      params = { page: page, pageSize: pageSize, importationStatus: importationStatus };
    let r = await this.api.get<any>("/v1/product/importation/", params);

    if (r != null && r.success) {
      this.setImportationHistory(r.data);
      return;
    }
    this.setImportationHistory([]);
  }

  private async apiGetValidatedProducts(importationId: number, pageSize: number | null = null, page: number | null = null, filter?: ImportedProductStatusEnum | null) {
    let r = await this.api.get<any>(new String().concat("/v1/product/importation/", importationId.toString(), "/products"),
      page != null && pageSize != null ? {pageSize: pageSize, page: page, productStatus: filter ?? ''} : null
    );

    if (!r.success) {
      this.alert.alertErrorMessage(r.message ?? this.translate.instant("api.internalServerError"));
      this.router.navigate(['/product'],{skipLocationChange:false})
      return;
    }
    return r.data;
  }

  private async apiPostCancelImportation(importationId: number) {
    let r = await this.api.post<any>("/v1/product/importation/"+importationId.toString()+"/cancel", null);
    if (!r.success) {
      this.alert.alertErrorMessage(r.message ?? this.translate.instant("api.internalServerError"));
      this._importation = null;
      return;
    }
    this._importation = null;
  }

  private async apiPostConfirmImportation(importationId: number): Promise<ProductImportation | null> {
    let r = await this.api.post<any>("/v1/product/importation/"+importationId.toString()+"/confirm", null);

    if (!r.success) {
      this.alert.alertErrorMessage(r.message ?? this.translate.instant("api.internalServerError"));
      return null;
    }
    return r.data;
  }

  public async apiGetProductImportationOriginalFileData(importationId: number): Promise<string[] | null> {
    let r = await this.api.get<any>("/v1/product/importation/"+importationId.toString()+"/original-file");

    if (!r.success || r.data == null) {
      this.alert.alertErrorMessage(r.message ?? this.translate.instant("api.internalServerError"));
      return null;
    }
    return r.data as string[];
  }

  // #endregion Api Calls Methods

  // #region Auxiliary Methods

  public removeHeaderEmptyColumnsAtEnd(headerLine: string): string {
    return headerLine.split(";").map((c) => {return c.trim();}).join(";").replace(/;+$/, "");
  }

  // #endregion Auxiliary Methods

}
